import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { timePeriodFromDateRange } from "components/usage/utils-usage";
import { DateRange as DateRangeEnum } from "contexts/models";
import dayjs from "dayjs";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import type { DateRange } from "react-day-picker";

export const DateRangePicker = (props: {
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined,
  ) => void;
  handleSubmit: (event?: React.FormEvent<HTMLFormElement> | undefined) => void;
  disabled: boolean;
  time: any;
  setTime: any;
  presetRange: any;
  setPresetRange: any;
  queryFilterState: any;
}) => {
  const [open, setOpen] = useState(false);
  const dayFormat = "DD/MM/YYYY";
  const queryFormat = "YYYY-MM-DD";

  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    undefined,
  );

  const handleRangeSelect = (startDate: Date, endDate: Date) => {
    props.setPresetRange("");
    props.setTime({
      ...props.time,
      startTime: startDate.getTime(),
      endTime: endDate.getTime(),
    });

    const release = `${dayjs(startDate.getTime()).format(queryFormat)},${dayjs(endDate.getTime()).format(queryFormat)}`;
    props.setFieldValue("release", release);
    props.handleSubmit();
    setOpen(false);
  };

  const handlePresetClick = (label: string, startDate: Date, endDate: Date) => {
    props.setPresetRange(label);
    setSelectedRange({ from: startDate, to: endDate });
    handleRangeSelect(startDate, endDate);
  };

  const handleCalendarSelect = (
    range: DateRange | undefined,
    triggerDate: Date,
  ) => {
    // If a full range is already selected, reset and start fresh from the clicked date
    if (selectedRange?.from && selectedRange?.to) {
      setSelectedRange({ from: triggerDate, to: undefined });
      return;
    }

    setSelectedRange(range);

    // Submit once both dates are picked and they differ
    if (
      range?.from &&
      range?.to &&
      range.from.getTime() !== range.to.getTime()
    ) {
      handleRangeSelect(range.from, range.to);
    }
  };

  const presetRanges = Object.values(DateRangeEnum).map((label) => {
    const [start, end] = timePeriodFromDateRange(label);
    return { label, startDate: new Date(start), endDate: new Date(end) };
  });

  const buttonLabel = props.queryFilterState.latestRelease
    ? "Filter by date"
    : props.presetRange
      ? props.presetRange
      : props.time.startTime === 0
        ? "Filter by date"
        : `${dayjs(props.time.startTime).format(dayFormat)} - ${dayjs(props.time.endTime).format(dayFormat)}`;

  return (
    <div className="flex flex-col gap-1.5 w-full">
      <Label htmlFor="date-picker-range">Date Range</Label>
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <button
            id="date-picker-range"
            type="button"
            disabled={props.disabled}
            className="flex w-full items-center justify-between rounded-md border border-border bg-transparent px-2 py-1 text-sm font-normal h-7 hover:bg-input/50 disabled:pointer-events-none disabled:opacity-50"
          >
            <span>{buttonLabel}</span>
            <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex">
            <div className="flex flex-col border-r p-2 gap-1">
              {presetRanges.map((preset) => (
                <Button
                  key={preset.label}
                  variant="ghost"
                  size="sm"
                  className="justify-start text-xs"
                  onClick={() =>
                    handlePresetClick(
                      preset.label,
                      preset.startDate,
                      preset.endDate,
                    )
                  }
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            <Calendar
              mode="range"
              selected={selectedRange}
              onSelect={handleCalendarSelect}
              numberOfMonths={2}
              captionLayout="dropdown"
              startMonth={new Date(2020, 0)}
              endMonth={new Date(2035, 11)}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
